/**
 * OpenTelemetry logs tracker for @spatius/avatarkit-rtc SDK.
 *
 * Piggybacks on the global LoggerProvider that @spatius/avatarkit registers via
 * `logs.setGlobalLoggerProvider()`. OTel's global registry is keyed by
 * `Symbol.for('io.opentelemetry.js.api.logs')` on `globalThis`, so this works
 * across separate `@opentelemetry/api-logs` copies — we don't need our own
 * exporter, batch processor, or backend config. avatarkit owns all of that
 * (OTLP HTTP → OpenObserve, batching, crash-replay). We only emit records.
 *
 * Mirrors the PostHog piggyback model in telemetry.ts: if avatarkit hasn't
 * initialized OTel yet, getLogger() resolves to a no-op provider and records
 * are dropped — acceptable, same as PostHog.
 *
 * @packageDocumentation
 * @internal
 */

import { logs, SeverityNumber } from '@opentelemetry/api-logs';
import type { AnyValue, AnyValueMap } from '@opentelemetry/api-logs';
import { clientContextFields } from '@spatius/avatarkit/internal-telemetry';

// Logger name distinguishes RTC-emitted records from web-sdk's
// ('spatius-avatarkit'); both land in the same OpenObserve stream.
const OTEL_LOGGER_NAME = 'spatius-avatarkit-rtc';

let sdkVersion = '1.0.0';

/** Set the SDK version stamped on emitted records. @internal */
export function setOtelVersion(version: string): void {
  if (version) sdkVersion = version;
}

function severityFromLevel(level: 'info' | 'warning' | 'error'): {
  severityNumber: SeverityNumber;
  severityText: string;
} {
  switch (level) {
    case 'warning':
      return { severityNumber: SeverityNumber.WARN, severityText: 'WARN' };
    case 'error':
      return { severityNumber: SeverityNumber.ERROR, severityText: 'ERROR' };
    case 'info':
    default:
      return { severityNumber: SeverityNumber.INFO, severityText: 'INFO' };
  }
}

function toAnyValue(value: unknown): AnyValue {
  if (value === null || value === undefined) return null;
  const t = typeof value;
  if (t === 'string' || t === 'number' || t === 'boolean') return value as AnyValue;
  if (Array.isArray(value)) return value.map(toAnyValue);
  if (t === 'object') {
    const out: AnyValueMap = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = toAnyValue(v);
    }
    return out;
  }
  // fallback: stringify functions / symbols / bigint
  return String(value);
}

function toAttributes(input: Record<string, unknown>): AnyValueMap {
  const out: AnyValueMap = {};
  for (const [k, v] of Object.entries(input)) {
    out[k] = toAnyValue(v);
  }
  return out;
}

/**
 * Force-export any buffered log records.
 *
 * avatarkit owns the provider and its batch processor, so `forceFlush` is not
 * guaranteed to be present and is called defensively; the global registry may
 * also hand back a proxy wrapping the real provider.
 * @internal
 */
export function flushOtelLogs(): void {
  try {
    type FlushableProvider = {
      forceFlush?: () => Promise<void>;
      getDelegate?: () => FlushableProvider;
    };
    const registered = logs.getLoggerProvider() as FlushableProvider;
    const provider = registered.getDelegate?.() ?? registered;
    provider.forceFlush?.().catch(() => {});
  } catch {
    // ignore
  }
}

/**
 * Emit one log record to the global OTel LoggerProvider (avatarkit's).
 * No-op (records dropped) if avatarkit hasn't registered a provider yet.
 * @internal
 */
export function trackEventOtel(
  event: string,
  level: 'info' | 'warning' | 'error',
  contents: Record<string, unknown>,
  timestamp: number,
): void {
  try {
    const otelLogger = logs.getLogger(OTEL_LOGGER_NAME, sdkVersion);
    const { severityNumber, severityText } = severityFromLevel(level);

    // Client environment (host/domain/url/ua/screen/viewport/timezone), taken
    // from avatarkit's own collection so the two SDKs cannot drift apart in
    // field names or in how each value is read — the backend aggregates on
    // exactly those names. Sharing avatarkit's LoggerProvider does not carry
    // these across: they are record-level, not Resource attributes, so without
    // this every RTC record reports no host at all.
    //
    // The call site's own properties come last and win on collision.
    const raw: Record<string, unknown> = {
      'event.name': event,
      ...clientContextFields(),
      ...contents,
    };

    // Pass timestamp as an HrTime tuple [seconds, nanos] rather than a number:
    // OTel's timeInputToHrTime() treats a number smaller than the page's
    // performance.timeOrigin as a performance.now() value and adds timeOrigin,
    // which misfires for replayed/old timestamps. Same guard web-sdk uses.
    const hrTime: [number, number] = [
      Math.trunc(timestamp / 1000),
      (timestamp % 1000) * 1_000_000,
    ];

    otelLogger.emit({
      timestamp: hrTime,
      severityNumber,
      severityText,
      body: event,
      attributes: toAttributes(raw),
    });
  } catch {
    // Never let telemetry break the caller.
  }
}
